/* Public offer projection. Native product-form remains the only cart writer. */
(function () {
  'use strict';
  const VERSION = 'TIM-PUBLIC-OFFER-1';
  const TEMPLATE_FAMILIES = ['BENEFIT_UNLOCK', 'PRICE_ARCHITECTURE', 'MULTIBUY_TIERS', 'LIMITED_WINDOW', 'CURATED_BUNDLE'];
  const SCOPES = ['PRODUCT', 'VARIANT', 'CATEGORY', 'COLLECTION', 'CART', 'STORE', 'STORE_WIDE'];
  const SURFACES = ['HOME', 'COLLECTION', 'PDP', 'CART'];
  const ANCHOR_KINDS = ['NONE', 'HISTORICAL_VERIFIED', 'MERCHANDISING_REFERENCE'];
  const money = value => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(value / 100);
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  const keys = (value, expected) => value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).sort().join('|') === expected.split(' ').sort().join('|');
  const date = value => typeof value === 'string' && /(?:Z|[+-]\d\d:\d\d)$/.test(value) ? Date.parse(value) : NaN;
  const canonical = value => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;
  const presBadge = pres => pres?.badge_tr || pres?.badge_text || null;
  const presHeadline = pres => pres?.headline_tr || pres?.headline || null;
  const presSubcopy = pres => pres?.subcopy_tr || pres?.subcopy || null;
  const presCta = pres => pres?.cta_hint_tr || pres?.cta_label || null;
  const presAnchorKind = pres => pres?.price_anchor_kind || pres?.anchor_kind || 'NONE';

  function validPresentation(pres, basePriceMinor) {
    if (pres === undefined || pres === null) return true;
    const isJsShape = keys(pres, 'template_family_id scope scope_target scope_label_tr surfaces price_anchor_kind reference_price_minor authorized_discount_bps eyebrow_tr headline_tr subcopy_tr badge_tr cta_hint_tr bundle_items');
    const isPyShape = keys(pres, 'template_family_id template_version scope scope_target scope_label_tr strategy_family badge_text headline subcopy cta_label anchor_kind reference_price_minor verified_prior_price_minor authorized_discount_bps bundle_items');
    if (!isJsShape && !isPyShape) return false;
    const anchorKind = presAnchorKind(pres);
    if (!TEMPLATE_FAMILIES.includes(pres.template_family_id) || !SCOPES.includes(pres.scope) || !ANCHOR_KINDS.includes(anchorKind)) return false;
    if (typeof pres.scope_target !== 'string' || pres.scope_target.length > 120) return false;
    if (typeof pres.scope_label_tr !== 'string' || pres.scope_label_tr.length > 120) return false;
    if (isJsShape) {
      if (!pres.scope_target.length || !pres.scope_label_tr.length) return false;
      if (!Array.isArray(pres.surfaces) || !pres.surfaces.length || pres.surfaces.length > 4 || pres.surfaces.some(s => !SURFACES.includes(s))) return false;
      for (const field of ['eyebrow_tr', 'headline_tr', 'subcopy_tr', 'badge_tr', 'cta_hint_tr']) {
        if (pres[field] !== null && (typeof pres[field] !== 'string' || !pres[field].length)) return false;
      }
    } else {
      if (!integer(pres.template_version) || pres.template_version < 1 || pres.template_version > 5) return false;
      if (typeof pres.strategy_family !== 'string' || !pres.strategy_family.length) return false;
      for (const field of ['badge_text', 'headline', 'subcopy', 'cta_label']) {
        if (typeof pres[field] !== 'string' || !pres[field].length) return false;
      }
    }
    if (pres.reference_price_minor !== null && (!integer(pres.reference_price_minor) || pres.reference_price_minor <= basePriceMinor)) return false;
    if (!integer(pres.authorized_discount_bps) || pres.authorized_discount_bps > 7000) return false;
    if (anchorKind !== 'NONE' && pres.reference_price_minor === null) return false;
    if (anchorKind === 'NONE' && pres.reference_price_minor !== null) return false;
    if (!Array.isArray(pres.bundle_items) || pres.bundle_items.length > 4) return false;
    if (pres.template_family_id === 'CURATED_BUNDLE' && !pres.bundle_items.length) return false;
    for (const item of pres.bundle_items) {
      const isJsBundle = keys(item, 'product_gid variant_gid title_tr unit_price_minor quantity') && /^gid:\/\/shopify\/ProductVariant\/\d+$/.test(item.variant_gid) && typeof item.title_tr === 'string' && item.title_tr.length > 0 && integer(item.quantity) && item.quantity >= 1 && item.quantity <= 4;
      const isPyBundle = keys(item, 'product_gid title unit_price_minor') && typeof item.title === 'string' && item.title.length > 0;
      if ((!isJsBundle && !isPyBundle) || !/^gid:\/\/shopify\/Product\/\d+$/.test(item.product_gid) || !integer(item.unit_price_minor) || item.unit_price_minor < 1) return false;
    }
    return true;
  }

  function validContract(offer, context, now = Date.now()) {
    const baseFields = 'contract_version status offer_id version offer_hash shop_domain product_gid currency starts_at ends_at base_unit_price_minor variant_ids design_profile tiers shipping_policy';
    const hasPresentation = offer && typeof offer === 'object' && Object.prototype.hasOwnProperty.call(offer, 'presentation');
    if (!keys(offer, hasPresentation ? baseFields + ' presentation' : baseFields)) return false;
    if (offer.contract_version !== VERSION || offer.status !== 'ACTIVE' || offer.currency !== 'TRY' || context.currency !== 'TRY') return false;
    const isStoreWide = offer?.scope === 'STORE_WIDE' || offer?.presentation?.scope === 'STORE_WIDE' || offer?.scope_target === 'tum-katalog' || context?.isStoreWide === true;
    if (offer.shop_domain !== context.shop) return false;
    if (!isStoreWide && offer.product_gid !== context.product) return false;
    if (!/^[a-f\d]{64}$/.test(offer.offer_hash) || !/^[a-f\d-]{36}$/i.test(offer.offer_id) || !integer(offer.version) || offer.version < 1) return false;
    if (!['EVERYDAY', 'SPORT', 'DECOR'].includes(offer.design_profile)) return false;
    if (!integer(offer.base_unit_price_minor) || offer.base_unit_price_minor < 1) return false;
    if (!(date(offer.starts_at) <= now && now < date(offer.ends_at)) || date(offer.ends_at) - date(offer.starts_at) > 7 * 86400000) return false;
    if (!Array.isArray(offer.variant_ids) || !offer.variant_ids.length || offer.variant_ids.length > 100 || offer.variant_ids.some(id => !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(id))) return false;
    if (!Array.isArray(offer.tiers) || !offer.tiers.length || offer.tiers.length > 4 || new Set(offer.tiers.map(t => t.quantity)).size !== offer.tiers.length) return false;
    if (!validPresentation(offer.presentation, offer.base_unit_price_minor)) return false;
    const shipping = offer.shipping_policy;
    if (shipping !== null && (!keys(shipping, 'source_id min_subtotal_minor verified_at valid_until combines_with_product_discounts') || !/^gid:\/\/shopify\/DiscountAutomaticNode\/\d+$/.test(shipping.source_id) || !integer(shipping.min_subtotal_minor) || typeof shipping.combines_with_product_discounts !== 'boolean' || !(date(shipping.verified_at) <= now && now < date(shipping.valid_until)))) return false;
    const pres = offer.presentation || null;
    return offer.tiers.every(tier => {
      if (!keys(tier, 'quantity total_minor claim benefit_type') || !integer(tier.quantity) || tier.quantity < 1 || tier.quantity > 4 || !integer(tier.total_minor)) return false;
      const fullTotal = offer.base_unit_price_minor * tier.quantity;
      if (tier.benefit_type === 'NONE') return tier.total_minor === fullTotal && tier.claim === 'Standart fiyat';
      if (tier.benefit_type === 'EXISTING_FREE_SHIPPING') return tier.total_minor === fullTotal && tier.claim === 'Mevcut ücretsiz kargo koşuluna uygun' && !!shipping && tier.total_minor >= shipping.min_subtotal_minor;
      if (tier.benefit_type === 'REFERENCE_PRICE_ADVANTAGE') return tier.total_minor === fullTotal && tier.claim === 'Referans fiyat avantajı' && !!pres && pres.template_family_id === 'PRICE_ARCHITECTURE' && integer(pres.reference_price_minor) && pres.reference_price_minor > offer.base_unit_price_minor;
      if (tier.benefit_type === 'TIER_PRICE_ADVANTAGE') return !!pres && pres.authorized_discount_bps > 0 && tier.total_minor > 0 && tier.total_minor < fullTotal && tier.claim === 'Çoklu alım fiyat avantajı';
      if (tier.benefit_type === 'LIMITED_WINDOW_ADVANTAGE') return !!pres && pres.template_family_id === 'LIMITED_WINDOW' && pres.authorized_discount_bps > 0 && tier.total_minor > 0 && tier.total_minor < fullTotal && tier.claim === 'Süreli kampanya avantajı';
      if (tier.benefit_type === 'CURATED_BUNDLE_ADVANTAGE') return !!pres && pres.template_family_id === 'CURATED_BUNDLE' && pres.authorized_discount_bps > 0 && tier.total_minor > 0 && tier.total_minor < fullTotal && tier.claim === 'Kombin paket avantajı';
      return false;
    });
  }

  async function validSeal(offer) {
    if (!globalThis.crypto?.subtle) return false;
    const { offer_hash, ...payload } = offer;
    if (payload.presentation === null || payload.presentation === undefined) {
      delete payload.presentation;
    }
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(canonical(payload))));
    return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('') === offer_hash;
  }

  const OFFER_PSYCHOLOGY_DESIGN_VERSION = 'TIM-OFFER-PSYCHOLOGY-DESIGN-1';
  const SEMANTIC_DANGER_ERROR_COLOR = '#B91C1C';
  const TEMPLATE_DESIGN_TOKENS = Object.freeze({
    BENEFIT_UNLOCK: Object.freeze({
      accentHex: '#0F6E56',
      textHex: '#065F46',
      surfaceHex: '#ECFDF5',
      surfaceStrongHex: '#D1FAE5',
      borderHex: '#A7F3D0',
      psychologyProfile: 'GAIN_CONFIRMATION_EMERALD',
    }),
    PRICE_ARCHITECTURE: Object.freeze({
      accentHex: '#8C6239',
      textHex: '#5C3D20',
      surfaceHex: '#FAF6F0',
      surfaceStrongHex: '#F4ECE1',
      borderHex: '#E5D7C3',
      psychologyProfile: 'VALUE_ANCHOR_CHAMPAGNE',
    }),
    MULTIBUY_TIERS: Object.freeze({
      accentHex: '#2B5B84',
      textHex: '#1E3A5F',
      surfaceHex: '#F0F6FB',
      surfaceStrongHex: '#E1EEF8',
      borderHex: '#BBD4EC',
      psychologyProfile: 'SMART_CHOICE_SAPPHIRE',
    }),
    LIMITED_WINDOW: Object.freeze({
      accentHex: '#B45309',
      textHex: '#92400E',
      surfaceHex: '#FFFBEB',
      surfaceStrongHex: '#FEF3C7',
      borderHex: '#FDE68A',
      psychologyProfile: 'CALM_URGENCY_AMBER',
    }),
    CURATED_BUNDLE: Object.freeze({
      accentHex: '#3730A3',
      textHex: '#312E81',
      surfaceHex: '#EEF2FF',
      surfaceStrongHex: '#E0E7FF',
      borderHex: '#C7D2FE',
      psychologyProfile: 'CURATED_HARMONY_INDIGO',
    }),
  });

  const ACADEMIC_OR_ROBOTIC_BLACKLIST = [
    'kategorik',
    'optimize edilmiş',
    'optimize edilmis',
    'dönüşüm oranı',
    'donusum orani',
    'ekonomik fayda',
    'shipping threshold',
    'quantity tier',
    'economic benefit',
    'canonical offer',
    'canonical',
    'tier',
    'variant',
    'kargo eşiğine uygun',
    'kargo esigine uygun',
    'kaçırılmayacak fırsat',
    'kacirilmayacak firsat',
    'şok indirim',
    'sok indirim',
  ];

  const DUPLICATE_CURRENCY_SENTENCE_RE = /\(\s*(?:₺\s*\d[\d.,]*|\d[\d.,]*\s*(?:tl|₺))\s*\)\s*(?:ile\s+)?(?:₺\s*\d[\d.,]*|\d[\d.,]*\s*(?:tl|₺))|(?:₺\s*\d[\d.,]*|\d[\d.,]*\s*(?:tl|₺))\s+ile\s+(?:₺\s*\d[\d.,]*|\d[\d.,]*\s*(?:tl|₺))\s+ücretsiz\s+kargo/i;
  const CURRENCY_TOKEN_RE = /₺\s*\d[\d.,]*|\b\d[\d.,]*\s*(?:TL|₺)\b/gi;

  function validateCustomerCopyNaturalness(text, options = {}) {
    const raw = String(text || '').trim();
    if (!raw) return { valid: false, violations: ['EMPTY_COPY'] };
    const lower = raw.toLowerCase();
    const violations = [];
    for (const term of ACADEMIC_OR_ROBOTIC_BLACKLIST) {
      if (lower.includes(term)) violations.push('BLACKLISTED_TERM:' + term);
    }
    if (DUPLICATE_CURRENCY_SENTENCE_RE.test(raw)) {
      violations.push('DUPLICATE_PARENTHETICAL_CURRENCY_IN_SENTENCE');
    }
    if (!options.allowMultiPriceComparison) {
      for (const sentence of raw.split(/(?<!\d)[.!?]+(?!\d)|\n+/)) {
        const matches = sentence.match(CURRENCY_TOKEN_RE);
        if (matches && matches.length > 1) {
          violations.push('MULTIPLE_CURRENCY_AMOUNTS_IN_SINGLE_SENTENCE');
          break;
        }
      }
    }
    return { valid: violations.length === 0, violations };
  }

  function safeCustomCopy(candidate, options = {}) {
    if (!candidate || typeof candidate !== 'string') return null;
    const trimmed = candidate.trim();
    if (!trimmed) return null;
    if (!validateCustomerCopyNaturalness(trimmed, options).valid) return null;
    if (options.requireGteThreshold && /\b([2-9])\s*adet(?:\s+al|\s+seçim|\b)|([2-9])\s*adette\b/i.test(trimmed) && !/\b[2-9]\+|\bve\s+üzeri|\bveya\s+daha\s+fazla/i.test(trimmed)) {
      return null;
    }
    return trimmed;
  }

  function resolveTemplateMeta(offer) {
    const pres = offer?.presentation || null;
    const family = pres?.template_family_id || 'BENEFIT_UNLOCK';
    const anchorKind = presAnchorKind(pres);
    const tokens = TEMPLATE_DESIGN_TOKENS[family] || TEMPLATE_DESIGN_TOKENS.BENEFIT_UNLOCK;
    const defaults = {
      BENEFIT_UNLOCK: {
        family: 'BENEFIT_UNLOCK',
        familyLabel: 'Kazanılmış Avantaj',
        eyebrow: pres?.eyebrow_tr || '✦ BİRLİKTE DAHA GÜZEL',
        railTitle: 'Adet & Kargo Avantajı Seçimi',
        badge: safeCustomCopy(presBadge(pres), { requireGteThreshold: true }) || 'Kargo Bizden',
        upperTag: 'AKTİF LASIRIUS AVANTAJI',
        ctaIdle: safeCustomCopy(presCta(pres)) || '2+ Adet Seç ➔',
        ctaActive: '✓ Avantaj Aktif',
        tokens,
      },
      PRICE_ARCHITECTURE: {
        family: 'PRICE_ARCHITECTURE',
        familyLabel: 'Fiyat Mimarisi & Konumlandırma',
        eyebrow: pres?.eyebrow_tr || '✦ SEÇKİN FİYAT KONUMLANDIRMASI',
        railTitle: 'Referans & Seçili Fiyat Seçimi',
        badge: safeCustomCopy(presBadge(pres)) || 'Özel Konumlandırma',
        upperTag: anchorKind === 'HISTORICAL_VERIFIED' ? 'DOĞRULANMIŞ ÖNCEKİ FİYAT AVANTAJI' : 'KOLEKSİYON REFERANS FİYAT KONUMU',
        ctaIdle: safeCustomCopy(presCta(pres)) || 'Seçimi Onayla ➔',
        ctaActive: '✓ Seçili Fiyat Aktif',
        tokens,
      },
      MULTIBUY_TIERS: {
        family: 'MULTIBUY_TIERS',
        familyLabel: 'Adet Arttıkça Avantaj',
        eyebrow: pres?.eyebrow_tr || '✦ AKILLI ADET SEÇİMİ',
        railTitle: 'Kademeli Adet & Birim Avantajı',
        badge: safeCustomCopy(presBadge(pres)) || 'Çoklu Alım Avantajı',
        upperTag: 'KADEMELİ ADET AVANTAJI',
        ctaIdle: safeCustomCopy(presCta(pres)) || 'Kademeyi Seç ➔',
        ctaActive: '✓ Kademe Aktif',
        tokens,
      },
      LIMITED_WINDOW: {
        family: 'LIMITED_WINDOW',
        familyLabel: 'Gerçek Süreli Teklif',
        eyebrow: pres?.eyebrow_tr || '✦ SINIRLI SÜRELİ SEÇKİ',
        railTitle: 'Süreli Kampanya Seçenekleri',
        badge: safeCustomCopy(presBadge(pres)) || 'Süreli Teklif',
        upperTag: 'DOĞRULANMIŞ SÜRELİ TEKLİF',
        ctaIdle: safeCustomCopy(presCta(pres)) || 'Süreli Avantajı Seç ➔',
        ctaActive: '✓ Süreli Teklif Aktif',
        tokens,
      },
      CURATED_BUNDLE: {
        family: 'CURATED_BUNDLE',
        familyLabel: 'Akıllı Kombin / Birlikte Al',
        eyebrow: pres?.eyebrow_tr || '✦ TAMAMLAYICI KOMBİN SEÇKİSİ',
        railTitle: 'Birlikte Al & Kombin Avantajı',
        badge: safeCustomCopy(presBadge(pres)) || 'Kombin Avantajı',
        upperTag: 'KÜRASYONLU KOMBİN AVANTAJI',
        ctaIdle: safeCustomCopy(presCta(pres)) || 'Kombini Uygula ➔',
        ctaActive: '✓ Kombin Seçili',
        tokens,
      },
    };
    return defaults[family] || defaults.BENEFIT_UNLOCK;
  }

  function calculatePdpVariantPricing(offer, variantPriceMinor, quantity = 2) {
    const unitMinor = integer(variantPriceMinor) && variantPriceMinor > 0
      ? variantPriceMinor
      : (integer(offer?.base_unit_price_minor) && offer.base_unit_price_minor > 0 ? offer.base_unit_price_minor : 0);
    const qty = integer(quantity) && quantity > 0 ? quantity : 1;
    const discountBps = integer(offer?.presentation?.authorized_discount_bps) ? offer.presentation.authorized_discount_bps : 0;
    const rawTotal = unitMinor * qty;
    const discountedTotal = qty > 1 && discountBps > 0
      ? Math.max(1, Math.round((rawTotal * (10000 - discountBps)) / 10000))
      : rawTotal;
    return {
      unitPriceMinor: unitMinor,
      quantity: qty,
      fullTotalMinor: rawTotal,
      totalMinor: discountedTotal,
      unitFormatted: money(unitMinor),
      totalFormatted: money(discountedTotal),
    };
  }

  function resolveSurfaceOfferCopy(offer, surface, options = {}) {
    const meta = resolveTemplateMeta(offer);
    const pres = offer?.presentation || null;
    const benefitTier = primaryBenefitTier(offer?.tiers || []) || { quantity: 2, total_minor: (offer?.base_unit_price_minor || 55000) * 2, benefit_type: 'EXISTING_FREE_SHIPPING' };
    const qty = integer(options.quantity) && options.quantity > 0 ? options.quantity : (benefitTier.quantity || 2);
    const qtyPlus = qty >= 2 ? qty + '+' : '1';
    const slug = String(options.slug || pres?.scope_target || '').toLowerCase();
    const scopeLabel = String(options.scopeLabel || pres?.scope_label_tr || 'Tayt Koleksiyonu');
    const isTayt = slug.includes('tayt') || scopeLabel.toLowerCase().includes('tayt');
    const isAvize = slug.includes('avize') || scopeLabel.toLowerCase().includes('avize') || scopeLabel.toLowerCase().includes('aydınlatma');
    const isStoreWide = offer?.scope === 'STORE_WIDE' || pres?.scope === 'STORE_WIDE' || pres?.scope_target === 'tum-katalog' || Boolean(options.isStoreWide) || String(options.scopeLabel || '').toLowerCase().includes('mağaza geneli') || String(options.scopeLabel || '').toLowerCase().includes('tüm kategoriler');
    const catNoun = isStoreWide ? 'Ürün' : (isTayt ? 'Tayt' : (isAvize ? 'Avize' : 'Ürün'));
    const catPlural = isStoreWide ? 'Tüm Modellerde' : (isTayt ? 'Taytlarda' : (isAvize ? 'Avizelerde' : (scopeLabel + ' Seçkisinde')));
    const isCategorySurface = surface === 'HOME' || surface === 'COLLECTION' || surface === 'COLLECTION_CARD';
    const showExactTotalPrice = !isCategorySurface && (surface === 'PDP' || surface === 'CART');
    const pdpPricing = showExactTotalPrice
      ? calculatePdpVariantPricing(offer, options.unitPriceMinor, qty)
      : null;

    if (surface === 'HOME') {
      const ctaText = isTayt ? 'Taytları İncele' : (isAvize ? 'Avizeleri İncele' : 'Koleksiyonu İncele');
      return {
        surface: 'HOME',
        showExactTotalPrice: false,
        exactTotalMinor: null,
        pill: qtyPlus + ' Üründe Kargo Bizden',
        headline: safeCustomCopy(presHeadline(pres), { requireGteThreshold: true }) || (catPlural + ' ' + qtyPlus + ' Üründe Kargo Bizden'),
        subcopy: safeCustomCopy(presSubcopy(pres), { requireGteThreshold: true }) || 'Uygun modellerden iki veya daha fazlasını seçtiğinizde standart kargo ücretini biz karşılıyoruz.',
        footerLabel: qtyPlus + ' ÜRÜN AVANTAJI',
        footerValue: 'Kargo Bizden',
        footerSub: 'Standart Kargo: 0 TL · ' + qty + ' ve üzeri modelde',
        cta: ctaText,
      };
    }

    if (surface === 'COLLECTION') {
      const ctaText = isTayt ? 'Modelleri İncele →' : (isAvize ? 'Avizeleri İncele →' : 'Modelleri İncele →');
      const headlineDefault = isStoreWide
        ? ('İki ve Üzeri Ürün Seçiminde Kargo Bizden')
        : ('İki ve Üzeri ' + catNoun + ' Seçiminde Kargo Bizden');
      const subcopyDefault = isStoreWide
        ? ('Bu koleksiyondaki veya mağazadaki modellerden ' + qty + ' veya daha fazla eklediğinizde standart kargo ücreti alınmaz.')
        : ('Bu koleksiyondaki uygun modellerden ' + qty + ' veya daha fazla eklediğinizde standart kargo ücreti alınmaz.');
      return {
        surface: 'COLLECTION',
        showExactTotalPrice: false,
        exactTotalMinor: null,
        pill: qtyPlus + ' ' + catNoun + ' Seç · Kargo Bizden',
        headline: safeCustomCopy(presHeadline(pres), { requireGteThreshold: true }) || headlineDefault,
        subcopy: safeCustomCopy(presSubcopy(pres), { requireGteThreshold: true }) || subcopyDefault,
        footerLabel: qtyPlus + ' ÜRÜN SEÇİMİNDE',
        footerValue: 'Kargo Bizden',
        footerSub: 'Standart Kargo: 0 TL',
        cta: ctaText,
      };
    }

    if (surface === 'COLLECTION_CARD') {
      let badgeText = meta.badge || 'Kargo Bizden';
      if (/^\s*\d+\+\s*(al\s*)?/i.test(badgeText)) {
        badgeText = badgeText.replace(/^\s*\d+\+\s*(al\s*)?/i, '').trim();
      }
      if (/kargo b[iİ]zden/i.test(badgeText)) {
        badgeText = 'Kargo Bizden';
      }
      const pillText = qty >= 2 ? (qtyPlus + ' Üründe ' + badgeText) : badgeText;
      return {
        surface: 'COLLECTION_CARD',
        showExactTotalPrice: false,
        exactTotalMinor: null,
        pill: pillText,
      };
    }

    if (surface === 'CART') {
      return {
        surface: 'CART',
        showExactTotalPrice: true,
        exactTotalMinor: pdpPricing ? pdpPricing.totalMinor : benefitTier.total_minor,
        pill: (qty >= 2 ? qtyPlus + ' Ürün' : '1 Ürün') + ' · ' + meta.badge,
        headline: 'Kargo Bizden · Standart Kargo: 0 TL',
        subcopy: 'Seçiminizle standart kargo ücreti alınmaz; ödeme adımında otomatik uygulanır.',
        footerSub: 'Standart Kargo: 0 TL',
      };
    }

    // PDP
    const totalMinor = integer(options.totalMinor) && options.totalMinor > 0
      ? options.totalMinor
      : (pdpPricing ? pdpPricing.totalMinor : benefitTier.total_minor);
    return {
      surface: 'PDP',
      showExactTotalPrice: true,
      exactUnitPriceMinor: pdpPricing ? pdpPricing.unitPriceMinor : offer?.base_unit_price_minor,
      exactTotalMinor: totalMinor,
      pill: meta.badge,
      headline: qty >= 2 ? (qty + ' ve Üzeri Seçimde Kargo Bizden') : 'Seçili Üründe Özel Avantaj',
      subcopy: qty + ' adet toplam ' + money(totalMinor) + ' · Standart Kargo: 0 TL',
      footerSub: 'Standart Kargo: 0 TL',
      cta: meta.ctaIdle,
    };
  }

  function eligibleTier(offer, variant, quantity, existingQuantity = 0) {
    const isStoreWide = offer?.scope === 'STORE_WIDE' || offer?.presentation?.scope === 'STORE_WIDE' || offer?.scope_target === 'tum-katalog';
    const variantMatch = isStoreWide || (variant.price === offer.base_unit_price_minor && offer.variant_ids.includes('gid://shopify/ProductVariant/' + variant.id));
    return !!variant && variant.available === true && variantMatch &&
      integer(quantity) && integer(existingQuantity) && quantity >= variant.min && variant.step > 0 && (quantity - variant.min) % variant.step === 0 && (variant.max === null || quantity + existingQuantity <= variant.max) && offer.tiers.some(tier => tier.quantity === quantity);
  }

  function eligibleQuantity(offer, variant, quantity, existingQuantity = 0) {
    const isStoreWide = offer?.scope === 'STORE_WIDE' || offer?.presentation?.scope === 'STORE_WIDE' || offer?.scope_target === 'tum-katalog';
    const variantMatch = isStoreWide || (variant.price === offer.base_unit_price_minor && offer.variant_ids.includes('gid://shopify/ProductVariant/' + variant.id));
    return !!variant && variant.available === true && variantMatch &&
      integer(quantity) && integer(existingQuantity) && quantity >= variant.min && variant.step > 0 && (quantity - variant.min) % variant.step === 0 && (variant.max === null || quantity + existingQuantity <= variant.max);
  }

  function cartTier(offer, cart) {
    const isStoreWide = offer?.scope === 'STORE_WIDE' || offer?.presentation?.scope === 'STORE_WIDE' || offer?.scope_target === 'tum-katalog';
    const variantMatch = isStoreWide || (cart.unit_price_minor === offer.base_unit_price_minor && offer.variant_ids.includes('gid://shopify/ProductVariant/' + cart.variant_id));
    if (!cart || cart.offer_id !== offer.offer_id || cart.offer_hash !== offer.offer_hash || Number(cart.tier_quantity) !== cart.quantity || !variantMatch) return null;
    const tier = offer.tiers.find(item => item.quantity === cart.quantity && (isStoreWide || item.total_minor === cart.line_total_minor));
    if (!tier) return null;
    if (tier.benefit_type === 'EXISTING_FREE_SHIPPING' && (cart.cart_subtotal_minor < offer.shipping_policy.min_subtotal_minor || (cart.has_discounts && !offer.shipping_policy.combines_with_product_discounts))) return null;
    return tier;
  }

  function primaryBenefitTier(tiers) {
    if (!Array.isArray(tiers) || !tiers.length) return null;
    return (
      tiers.find(tier => tier.benefit_type && tier.benefit_type !== 'NONE') ||
      tiers.find(tier => tier.quantity > 1) ||
      tiers[0]
    );
  }

  function formatRemainingWindow(untilMs, nowMs = Date.now()) {
    const diff = untilMs - nowMs;
    if (!(diff > 0) || !Number.isFinite(diff)) return null;
    const totalMinutes = Math.max(1, Math.ceil(diff / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours >= 48) {
      const days = Math.floor(hours / 24);
      return days + ' gün geçerli';
    }
    if (hours >= 1) {
      return minutes > 0 ? hours + ' sa ' + minutes + ' dk geçerli' : hours + ' saat geçerli';
    }
    return 'Son ' + totalMinutes + ' dk';
  }

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null && text !== '') element.textContent = text;
    return element;
  }

  function benefitPill(label, extraClass) {
    const cleanLabel = String(label || '').replace(/^\s*✓\s*/, '');
    const pill = node('span', 'teklif-benefit-pill' + (extraClass ? ' ' + extraClass : ''));
    const icon = node('span', 'teklif-benefit-pill__icon', '✓');
    icon.setAttribute('aria-hidden', 'true');
    pill.append(icon, node('span', 'teklif-benefit-pill__text', cleanLabel));
    return pill;
  }

  function cleanDisplayTitle(rawTitle) {
    if (!rawTitle) return 'Seçili Lasirius Tasarımı';
    const base = String(rawTitle).split('|')[0].split(' – ')[0].split(' — ')[0].trim();
    return base.length > 58 ? base.slice(0, 56).trim() + '…' : base;
  }

  function shortenProductTitle(rawTitle) {
    if (!rawTitle) return 'Seçili Model';
    let s = String(rawTitle).split('|')[0].split(' – ')[0].split(' — ')[0].trim();
    s = s.replace(/^(?:Lasirius\s+)?(?:Kadın\s+)?(?:Siyah\s+|Lacivert\s+|Bordo\s+|Kahverengi\s+|Antrasit\s+|Bej\s+|Beyaz\s+)?/i, '').trim();
    s = s.replace(/^Yüksek\s+Bel\s+Toparlayıcı\s+/i, '').trim();
    if (!s) s = cleanDisplayTitle(rawTitle);
    return s.length > 32 ? s.slice(0, 30).trim() + '…' : s;
  }

  let fairyStateObserverBound = false;
  let fairyIntersectionObserver = null;
  const observedOfferNodes = new WeakSet();

  function syncFairyOfferYield() {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    const fairy = document.querySelector('[data-merlin-fairy]');
    if (!fairy) return;

    if (!fairyStateObserverBound) {
      fairyStateObserverBound = true;
      if (typeof MutationObserver !== 'undefined') {
        const obs = new MutationObserver(() => syncFairyOfferYield());
        obs.observe(fairy, { attributes: true, attributeFilter: ['data-state'] });
      }
      if (typeof window.addEventListener === 'function') {
        window.addEventListener('scroll', () => syncFairyOfferYield(), { passive: true, capture: true });
        window.addEventListener('resize', () => syncFairyOfferYield(), { passive: true });
      }
      if (typeof IntersectionObserver !== 'undefined') {
        fairyIntersectionObserver = new IntersectionObserver(
          () => syncFairyOfferYield(),
          { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
        );
      }
    }

    const offerSurfaces = Array.from(
      document.querySelectorAll(
        '.lasirius-campaign-card, .lasirius-collection-offer, .teklif-ust-bant, lasirius-offer:not([hidden]):not([data-mode="cart"]):not([data-mode="card"]), .lasirius-smart-shipping, [data-cart-offer]'
      )
    );
    if (fairyIntersectionObserver) {
      for (const el of offerSurfaces) {
        if (!observedOfferNodes.has(el)) {
          observedOfferNodes.add(el);
          fairyIntersectionObserver.observe(el);
        }
      }
    }
    const headerSurfaces = Array.from(
      document.querySelectorAll('.lasirius-campaigns__header')
    );

    const visibleRects = (els) =>
      els
        .map(el => el.getBoundingClientRect())
        .filter(r => r && r.width > 4 && r.height > 4 && r.bottom > 0 && r.top < window.innerHeight);

    const offerRects = visibleRects(offerSurfaces);
    const headerRects = visibleRects(headerSurfaces);
    const allProtectedRects = [...offerRects, ...headerRects];

    if (!offerRects.length) {
      delete fairy.dataset.offerYield;
      return;
    }

    const mobile = window.innerWidth <= 740;
    const fW = parseFloat(fairy.style.getPropertyValue('--fairy-width')) || (mobile ? 120 : 160);
    const fH = parseFloat(fairy.style.getPropertyValue('--fairy-height')) || (mobile ? 180 : 240);
    const baseX = parseFloat(fairy.style.getPropertyValue('--fairy-x'));
    const baseY = parseFloat(fairy.style.getPropertyValue('--fairy-y'));
    const currentRect = fairy.getBoundingClientRect();
    const naturalBox = Number.isFinite(baseX) && Number.isFinite(baseY)
      ? { left: baseX - 88, top: baseY - 168, right: baseX + fW + 88, bottom: baseY + fH + 16 }
      : { left: currentRect.left - 88, top: currentRect.top - 168, right: currentRect.right + 88, bottom: currentRect.bottom + 16 };

    const boxIntersects = (box, r) => !(box.right <= r.left || box.left >= r.right || box.bottom <= r.top || box.top >= r.bottom);
    const hitsOffer = allProtectedRects.some(r => boxIntersects(naturalBox, r));

    if (!hitsOffer) {
      delete fairy.dataset.offerYield;
      return;
    }

    const scale = mobile ? 0.5 : 0.68;
    const scaledW = Math.round(fW * scale);
    const scaledH = Math.round(fH * scale);
    const rightX = Math.max(8, window.innerWidth - scaledW - 10);
    const bottomY = Math.max(10, window.innerHeight - scaledH - 10);

    const candidates = [
      { x: rightX, y: bottomY },
      { x: rightX, y: 10 },
      { x: 10, y: bottomY },
      { x: 10, y: 10 },
    ];

    for (const r of offerRects) {
      if (r.bottom + scaledH + 12 <= window.innerHeight) {
        candidates.push({ x: rightX, y: Math.round(r.bottom + 6) });
      }
      if (r.top - scaledH - 12 >= 8) {
        candidates.push({ x: rightX, y: Math.round(r.top - scaledH - 6) });
      }
    }

    let chosen = null;
    for (const cand of candidates) {
      const candBox = { left: cand.x - 2, top: cand.y - 2, right: cand.x + scaledW + 2, bottom: cand.y + scaledH + 2 };
      if (!allProtectedRects.some(r => boxIntersects(candBox, r))) {
        chosen = cand;
        break;
      }
    }
    if (!chosen) {
      for (const cand of candidates) {
        const candBox = { left: cand.x, top: cand.y, right: cand.x + scaledW, bottom: cand.y + scaledH };
        if (!offerRects.some(r => boxIntersects(candBox, r))) {
          chosen = cand;
          break;
        }
      }
    }
    if (!chosen) chosen = candidates[0];

    fairy.style.setProperty('--fairy-yield-x', chosen.x + 'px');
    fairy.style.setProperty('--fairy-yield-y', chosen.y + 'px');
    fairy.style.setProperty('--fairy-yield-scale', String(scale));
    fairy.dataset.offerYield = 'true';
  }

  function categorySlugForItem(item) {
    const pres = item?.offer?.presentation;
    if (pres?.scope === 'CATEGORY' && pres.scope_target) return String(pres.scope_target).toLowerCase();
    const cat = String(item?.category || '').toLowerCase();
    if (cat.includes('tayt')) return 'taytlar';
    if (cat.includes('avize') || cat.includes('aydınlatma')) return 'avize';
    return cat || 'all';
  }

  function categoryLabelForSlug(slug, fallback) {
    if (slug === 'taytlar' || slug === 'tayt') return 'Tayt Koleksiyonu';
    if (slug === 'avize') return 'Avize & Aydınlatma Koleksiyonu';
    return fallback || 'Seçili Koleksiyon';
  }

  class LasiriusOffer extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form = this.closest('form');
      this.form?.addEventListener('change', () => this.refresh(), options);
      this.form?.addEventListener('quantity-selector:update', () => this.refresh(), options);
      this.form?.addEventListener('submit', () => this.syncProperties(), { ...options, capture: true });
      if (typeof document !== 'undefined') {
        document.addEventListener('shopify:product:select', event => this.variantChange(event), options);
        document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
      }
      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('resize', () => this.syncFairy(), { ...options, passive: true });
        window.addEventListener('scroll', () => this.syncFairy(), { ...options, passive: true });
      }
      this.read();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.expiryTimer);
      clearInterval(this.countdownInterval);
      this.exposureObserver?.disconnect();
      this.clearProperties();
      this.clearUpperBanner();
      if (typeof document !== 'undefined') {
        const fairy = document.querySelector('[data-merlin-fairy]');
        if (fairy) delete fairy.dataset.offerYield;
      }
    }

    async variantChange(event) {
      const owner = this.closest('.shopify-section, dialog, product-card, section');
      const sourceCard = event.target?.closest('product-card');
      if (!this.form || !owner?.contains(event.target) || (sourceCard && !sourceCard.contains(this))) return;
      const generation = this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      this.clearProperties();
      this.clearUpperBanner();
      try {
        const { detail } = await event.promise;
        if (!this.isConnected || generation !== this.generation) return;
        const current = detail?.html?.querySelector('lasirius-offer[data-product-gid="' + this.dataset.productGid + '"]');
        if (!current) return;
        for (const attribute of ['data-offer-contract', 'data-offer-variants']) {
          const source = current.querySelector('[' + attribute + ']');
          if (!source) return;
          this.querySelector('[' + attribute + ']').textContent = source.textContent;
        }
        this.read();
      } catch (_) {
        this.hidden = true;
        this.clearUpperBanner();
      }
    }

    context() {
      return {
        product: this.dataset.productGid,
        shop: this.dataset.shopDomain,
        currency: this.dataset.currency,
        isStoreWide: this.dataset.isStoreWide === 'true',
      };
    }

    effectiveUntil() {
      if (!this.offer) return NaN;
      return Math.min(date(this.offer.ends_at), this.offer.shipping_policy ? date(this.offer.shipping_policy.valid_until) : Infinity);
    }

    async read() {
      const generation = this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      this.verified = false;
      this.clearProperties();
      this.clearUpperBanner();
      clearTimeout(this.expiryTimer);
      clearInterval(this.countdownInterval);
      try {
        this.offer = JSON.parse(this.querySelector('[data-offer-contract]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-offer-variants]')?.textContent || '[]');
        this.cart = JSON.parse(this.querySelector('[data-offer-cart]')?.textContent || 'null');
        if (!validContract(this.offer, this.context()) || !(await validSeal(this.offer)) || !this.isConnected || generation !== this.generation) return;
        this.verified = true;
        const until = this.effectiveUntil();
        this.expiryTimer = setTimeout(() => this.refresh(), Math.max(1, until - Date.now() + 1));
        this.applyUrlOfferSelectionOnce();
        this.refresh();
      } catch (_) {
        this.hidden = true;
        this.clearUpperBanner();
      }
    }

    applyUrlOfferSelectionOnce() {
      if (this._urlTierApplied || this.dataset.mode === 'cart' || this.dataset.mode === 'card') return;
      if (typeof window === 'undefined' || !window.location?.search) return;
      try {
        const params = new URLSearchParams(window.location.search);
        const requested = Number(params.get('merlin_offer_tier'));
        if (!integer(requested) || requested < 1) return;
        const current = this.state();
        if (!current.input || !eligibleTier(this.offer, current.variant, requested, current.existing)) return;
        this._urlTierApplied = true;
        const selector = current.input.closest?.('quantity-selector-component');
        if (selector?.setValue) selector.setValue(requested);
        else current.input.value = String(requested);
      } catch (_) { /* ignore malformed URL params */ }
    }

    state() {
      const form = this.form || this.closest('form');
      const section = this.closest('.shopify-section, main, [data-section-type], section') || document;
      const quantityInput = form?.querySelector?.('[name="quantity"]') || section.querySelector?.('form [name="quantity"], [name="quantity"]');
      const idInput = form?.querySelector?.('[name="id"]') || section.querySelector?.('form [name="id"], [name="id"]');
      const id = idInput?.value;
      const rawQty = Number(quantityInput?.value);
      const quantity = Number.isSafeInteger(rawQty) && rawQty > 0 ? rawQty : 1;
      const variant = this.variants?.find(item => item.id === id) || this.variants?.[0] || null;
      return {
        input: quantityInput || { value: String(quantity), disabled: false },
        variant,
        quantity,
        existing: Number(quantityInput?.dataset?.cartQuantity || 0),
      };
    }

    clearProperties() { this.form?.querySelectorAll('[data-merlin-offer-property]').forEach(input => input.remove()); }

    syncProperties() {
      this.clearProperties();
      if (this.hasAttribute('data-preview') || !this.verified || this.hidden || !validContract(this.offer, this.context())) return;
      const state = this.state();
      if (!eligibleTier(this.offer, state.variant, state.quantity, state.existing)) return;
      const meta = resolveTemplateMeta(this.offer);
      for (const [key, value] of Object.entries({
        _merlin_offer_id: this.offer.offer_id,
        _merlin_offer_version: this.offer.version,
        _merlin_offer_hash: this.offer.offer_hash,
        _merlin_offer_quantity: state.quantity,
        _merlin_offer_template: meta.family,
      })) {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = 'properties[' + key + ']'; input.value = value;
        input.dataset.merlinOfferProperty = '';
        this.form.append(input);
      }
    }

    emit(kind, quantity) {
      const surface = this.dataset.mode === 'card' ? 'COLLECTION_CARD' : 'PDP';
      const stage = { render: 'ASSIGNED', exposure: 'OBSERVED', tier_selection: 'SELECTED' }[kind];
      const meta = resolveTemplateMeta(this.offer);
      const copyVariant = meta.family + '.COPY.V2';
      const psychologyProfile = meta.tokens?.psychologyProfile || 'GAIN_CONFIRMATION_EMERALD';
      this.dispatchEvent(new CustomEvent('lasirius:offer', {
        bubbles: true,
        detail: {
          event: kind,
          offer_id: this.offer.offer_id,
          version: this.offer.version,
          offer_hash: this.offer.offer_hash,
          template_family_id: meta.family,
          copy_variant: copyVariant,
          psychology_profile: psychologyProfile,
          product_gid: this.offer.product_gid,
          surface,
          stage,
          quantity,
        },
      }));
      if (!this.hasAttribute('data-preview') && window.Shopify?.analytics?.publish) {
        try {
          const pending = window.Shopify.analytics.publish('lasirius_offer', {
            offer_passport: {
              offer_id: this.offer.offer_id,
              version: this.offer.version,
              offer_hash: this.offer.offer_hash,
              template_family_id: meta.family,
              copy_variant: copyVariant,
              psychology_profile: psychologyProfile,
              surface,
              stage,
            },
            product_id: this.offer.product_gid,
            variant_id: this.state().variant ? 'gid://shopify/ProductVariant/' + this.state().variant.id : null,
            quantity,
          });
          pending?.catch?.(() => {});
        } catch (_) { /* The existing pixel owns consent and delivery. */ }
      }
    }

    syncFairy() {
      syncFairyOfferYield();
    }

    ensureUpperSlot() {
      if (typeof document === 'undefined' || this.dataset.mode === 'cart' || this.dataset.mode === 'card') return null;
      const root = this.closest('.product-information, main, .shopify-section, section, body') || document.body;
      let slot = root?.querySelector('[data-lasirius-offer-upper]');
      const priceParent = slot?.closest?.('product-price');
      if (slot && priceParent && priceParent.parentElement) {
        priceParent.insertAdjacentElement('afterend', slot);
      }
      if (!slot && root) {
        const priceEl = root.querySelector('product-price, .price');
        if (priceEl && priceEl.parentElement) {
          slot = document.createElement('div');
          slot.className = 'teklif-ust-yuva';
          slot.dataset.lasiriusOfferUpper = '';
          slot.hidden = true;
          priceEl.insertAdjacentElement('afterend', slot);
        }
      }
      return slot || null;
    }

    clearUpperBanner() {
      if (typeof document === 'undefined') return;
      const root = this.closest('.product-information, main, .shopify-section, section, body') || document;
      root?.querySelectorAll?.('[data-lasirius-offer-upper]').forEach(slot => {
        slot.hidden = true;
        slot.replaceChildren();
      });
    }

    selectTierQuantity(quantity) {
      if (!validContract(this.offer, this.context())) return this.refresh();
      const current = this.state();
      let input = current.input;
      if (!input || typeof input.dispatchEvent !== 'function') {
        input = this.form?.querySelector?.('[name="quantity"]');
        if (!input && this.form) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'quantity';
          input.value = '1';
          this.form.append(input);
        }
      }
      const isContractTier = eligibleTier(this.offer, current.variant, quantity, current.existing);
      const isBaselineSingle = quantity === 1 && eligibleQuantity(this.offer, current.variant, 1, current.existing);
      if (!isContractTier && !isBaselineSingle) return this.refresh();
      const selector = input?.closest?.('quantity-selector-component');
      if (selector?.setValue) selector.setValue(quantity);
      else if (input) input.value = String(quantity);
      if (input && typeof input.dispatchEvent === 'function') {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (isContractTier) this.emit('tier_selection', quantity);
      this.refresh();
      this.querySelector('[data-quantity="' + quantity + '"]')?.focus({ preventScroll: true });
    }

    buildTierCopy(tier, meta, activeUnitPriceMinor) {
      const pres = this.offer?.presentation || null;
      const unitMinor = integer(activeUnitPriceMinor) && activeUnitPriceMinor > 0
        ? activeUnitPriceMinor
        : this.offer.base_unit_price_minor;
      const fullTotal = unitMinor * tier.quantity;
      const effectiveTotal = unitMinor === this.offer.base_unit_price_minor
        ? tier.total_minor
        : calculatePdpVariantPricing(this.offer, unitMinor, tier.quantity).totalMinor;
      const savedMinor = Math.max(0, fullTotal - effectiveTotal);

      if (tier.benefit_type === 'EXISTING_FREE_SHIPPING') {
        const pdpCopy = resolveSurfaceOfferCopy(this.offer, 'PDP', {
          unitPriceMinor: unitMinor,
          quantity: tier.quantity,
          totalMinor: effectiveTotal,
        });
        return {
          pill: pdpCopy.pill,
          headline: pdpCopy.headline,
          subcopy: pdpCopy.subcopy,
          footerSub: pdpCopy.footerSub,
        };
      }
      if (tier.benefit_type === 'REFERENCE_PRICE_ADVANTAGE' && pres?.reference_price_minor) {
        const refDiff = Math.max(0, (pres.reference_price_minor - unitMinor) * tier.quantity);
        const anchorPrefix = presAnchorKind(pres) === 'HISTORICAL_VERIFIED' ? 'Doğrulanmış önceki fiyat' : 'Koleksiyon referansı';
        const headline = safeCustomCopy(presHeadline(pres), { allowMultiPriceComparison: true }) || ('Seçkin Fiyat Konumu: ' + money(unitMinor));
        const subcopy = safeCustomCopy(presSubcopy(pres), { allowMultiPriceComparison: true }) || (tier.quantity + '+ adet seçimde toplam ' + money(effectiveTotal) + ' · Standart Kargo: 0 TL');
        return { pill: safeCustomCopy(presBadge(pres)) || (money(refDiff) + ' Avantaj'), headline, subcopy, footerSub: anchorPrefix + ': ' + money(pres.reference_price_minor) };
      }
      if (tier.benefit_type === 'TIER_PRICE_ADVANTAGE') {
        const headline = safeCustomCopy(presHeadline(pres)) || (tier.quantity + '+ Adet Seçimde ' + money(savedMinor) + ' Avantaj');
        const subcopy = safeCustomCopy(presSubcopy(pres)) || (tier.quantity + ' adet toplam ' + money(effectiveTotal) + ' · Standart Kargo: 0 TL');
        return { pill: safeCustomCopy(presBadge(pres)) || (money(savedMinor) + ' Avantaj'), headline, subcopy, footerSub: 'Birim: ' + money(Math.round(effectiveTotal / tier.quantity)) + '/adet' };
      }
      if (tier.benefit_type === 'LIMITED_WINDOW_ADVANTAGE') {
        const headline = safeCustomCopy(presHeadline(pres)) || ('Süreli Kampanya Fiyatı: ' + money(effectiveTotal));
        const subcopy = safeCustomCopy(presSubcopy(pres)) || ('Süre içinde ' + tier.quantity + '+ adet toplam ' + money(effectiveTotal) + '.');
        return { pill: safeCustomCopy(presBadge(pres)) || 'Süreli Avantaj', headline, subcopy, footerSub: 'Süreli seçki · ' + money(savedMinor) + ' avantaj' };
      }
      if (tier.benefit_type === 'CURATED_BUNDLE_ADVANTAGE') {
        const headline = safeCustomCopy(presHeadline(pres)) || ('Birlikte Al Kombin Avantajı: ' + money(effectiveTotal));
        const subcopy = safeCustomCopy(presSubcopy(pres)) || ('Uyumlu parçalarla birlikte alındığında ' + money(savedMinor) + ' kombin avantajı sağlanır.');
        return { pill: safeCustomCopy(presBadge(pres)) || 'Kombin Avantajı', headline, subcopy, footerSub: 'Tamamlayıcı kombin · ' + money(savedMinor) + ' avantaj' };
      }
      return {
        pill: meta.badge,
        headline: safeCustomCopy(presHeadline(pres)) || (tier.quantity + '+ Adet Seçimde Özel Avantaj'),
        subcopy: safeCustomCopy(presSubcopy(pres)) || ('Toplam ' + money(effectiveTotal) + ' tutarında doğrulanmış teklif.'),
        footerSub: 'Standart fiyat',
      };
    }

    renderUpperBanner(eligibleTiers, state, windowText, meta) {
      const slot = this.ensureUpperSlot();
      if (!slot) return;
      slot.replaceChildren();
      const primaryBenefit = primaryBenefitTier(eligibleTiers);
      if (!primaryBenefit || (primaryBenefit.benefit_type === 'NONE' && primaryBenefit.quantity === 1 && !this.offer.presentation)) {
        slot.hidden = true;
        return;
      }
      const isBenefitActive = state.quantity >= primaryBenefit.quantity && eligibleTier(this.offer, state.variant, state.quantity, state.existing);
      const activeUnitMinor = state.variant?.price || this.offer.base_unit_price_minor;
      const copy = this.buildTierCopy(primaryBenefit, meta, activeUnitMinor);
      const banner = node('div', 'teklif-ust-bant' + (isBenefitActive ? ' teklif-ust-bant--aktif' : ''));
      banner.dataset.templateFamily = meta.family;
      banner.setAttribute('role', 'region');
      banner.setAttribute('aria-label', 'Aktif ürün avantajı');

      const body = node('div', 'teklif-ust-bant__icerik');
      const topRow = node('div', 'teklif-ust-bant__ust');
      topRow.append(
        benefitPill(copy.pill, 'teklif-benefit-pill--hero'),
        node('span', 'teklif-ust-bant__etiket', meta.upperTag)
      );
      if (windowText) {
        topRow.append(node('span', 'teklif-timer-pill', windowText));
      }

      body.append(
        topRow,
        node('p', 'teklif-ust-bant__baslik', copy.headline),
        node('p', 'teklif-ust-bant__aciklama', copy.subcopy)
      );

      if (meta.family === 'PRICE_ARCHITECTURE' && this.offer.presentation?.reference_price_minor) {
        const anchorRow = node('div', 'teklif-price-anchor');
        const anchorLabel = presAnchorKind(this.offer.presentation) === 'HISTORICAL_VERIFIED'
          ? 'Önceki Doğrulanmış Fiyat:'
          : 'Koleksiyon Referans Konumu:';
        anchorRow.append(
          node('span', 'teklif-price-anchor__label', anchorLabel),
          node('s', 'teklif-price-anchor__strike', money(this.offer.presentation.reference_price_minor * primaryBenefit.quantity)),
          node('strong', 'teklif-price-anchor__active', money(primaryBenefit.total_minor))
        );
        body.append(anchorRow);
      }

      const actionWrap = node('div', 'teklif-ust-bant__sag');
      const actionBtn = node(
        'button',
        'teklif-ust-aksiyon' + (isBenefitActive ? ' teklif-ust-aksiyon--aktif' : ''),
        isBenefitActive ? meta.ctaActive : meta.ctaIdle
      );
      actionBtn.type = 'button';
      actionBtn.dataset.upperOfferAction = String(primaryBenefit.quantity);
      actionBtn.setAttribute('aria-pressed', String(isBenefitActive));
      actionBtn.addEventListener('click', () => {
        this.selectTierQuantity(primaryBenefit.quantity);
      });
      actionWrap.append(actionBtn);
      banner.append(body, actionWrap);
      slot.append(banner);
      slot.hidden = false;
    }

    refresh() {
      this.hidden = true;
      this.clearProperties();
      this.clearUpperBanner();
      this.syncFairy();
      if (typeof document !== 'undefined') {
        document.querySelectorAll('[data-merlin-offer-context]').forEach(context => {
          if (context.dataset.merlinOfferContext === this.dataset.productGid) context.remove();
        });
      }
      if (!this.verified || !validContract(this.offer, this.context())) return;
      const view = this.querySelector('[data-offer-view]');
      if (!view) return;
      view.replaceChildren();
      const meta = resolveTemplateMeta(this.offer);
      this.dataset.templateFamily = meta.family;

      if (this.dataset.mode === 'cart') {
        const tier = cartTier(this.offer, this.cart);
        if (!tier) return;
        const cartCopy = resolveSurfaceOfferCopy(this.offer, 'CART', {
          quantity: tier.quantity,
          unitPriceMinor: this.cart?.unit_price_minor || this.offer.base_unit_price_minor,
        });
        const cartBox = node('div', 'teklif-cart');
        cartBox.dataset.templateFamily = meta.family;
        if (tier.benefit_type !== 'NONE') {
          cartBox.append(
            benefitPill(cartCopy.pill, 'teklif-benefit-pill--cart'),
            node('span', 'teklif-cart__meta', money(tier.total_minor) + ' · ' + cartCopy.footerSub)
          );
        } else {
          cartBox.append(node('span', 'teklif-cart__meta', tier.quantity + ' adet · ' + money(tier.total_minor) + ' · Standart fiyat'));
        }
        view.append(cartBox);
        this.hidden = false;
        return;
      }

      if (this.dataset.mode === 'card') {
        const benefitTier = primaryBenefitTier(this.offer.tiers);
        const isStoreWide = this.offer.scope === 'STORE_WIDE' || this.offer.presentation?.scope === 'STORE_WIDE' || this.offer.scope_target === 'tum-katalog' || this.dataset.isStoreWide === 'true';
        const hasEligibleVariant = benefitTier && (this.variants || []).some(v => v.available === true && (isStoreWide || eligibleTier(this.offer, v, benefitTier.quantity, 0)));
        if (!benefitTier || !hasEligibleVariant) return;
        const cardCopy = resolveSurfaceOfferCopy(this.offer, 'COLLECTION_CARD', {
          quantity: benefitTier.quantity,
        });
        const badge = benefitPill(cardCopy.pill, 'teklif-benefit-pill--card');
        badge.dataset.templateFamily = meta.family;
        view.append(badge);
        const card = this.closest('.lasirius-card, .product-card, product-card');
        const price = card?.querySelector('product-price');
        if (price && price.parentElement === this.parentElement && price.previousElementSibling !== this) {
          price.before(this);
        }
        card?.querySelectorAll('a[href*="/products/"]').forEach(link => {
          try {
            const url = new URL(link.getAttribute('href'), window.location.origin);
            url.searchParams.set('merlin_offer_tier', String(benefitTier.quantity));
            link.setAttribute('href', url.pathname + url.search);
          } catch (_) { /* preserve existing href */ }
        });
        this.hidden = false;
        return;
      }

      const state = this.state();
      const isStoreWide = this.offer.scope === 'STORE_WIDE' || this.offer.presentation?.scope === 'STORE_WIDE' || this.offer.scope_target === 'tum-katalog' || this.dataset.isStoreWide === 'true';
      const contractTiers = this.offer.tiers.filter(tier => isStoreWide ? Boolean(state.variant?.available) : eligibleTier(this.offer, state.variant, tier.quantity, state.existing));
      if (!state.input || state.input.disabled || !contractTiers.length) return;
      const activeUnitMinor = state.variant?.price || this.offer.base_unit_price_minor;

      const displayTiers = [...contractTiers];
      if (!displayTiers.some(tier => tier.quantity === 1) && eligibleQuantity(this.offer, state.variant, 1, state.existing)) {
        displayTiers.unshift({
          quantity: 1,
          total_minor: activeUnitMinor,
          claim: 'Standart fiyat',
          benefit_type: 'NONE',
          _baseline: true,
        });
      }

      this.dataset.profile = this.offer.design_profile.toLowerCase();
      const windowText = formatRemainingWindow(this.effectiveUntil());
      this.renderUpperBanner(contractTiers, state, windowText, meta);

      const header = node('div', 'teklif-header');
      const headerLeft = node('div', 'teklif-header__left');
      headerLeft.append(
        node('span', 'teklif-eyebrow', meta.eyebrow),
        node('strong', 'teklif-title', meta.railTitle)
      );
      const headerRight = node('div', 'teklif-header__right');
      if (windowText) {
        const timerBadge = node('span', 'teklif-timer-pill', windowText);
        timerBadge.dataset.offerTimer = '';
        headerRight.append(timerBadge);
      }
      headerRight.append(node('span', 'teklif-caption', 'Adedinizi seçin'));
      header.append(headerLeft, headerRight);
      view.append(header);

      if (meta.family === 'CURATED_BUNDLE' && Array.isArray(this.offer.presentation?.bundle_items) && this.offer.presentation.bundle_items.length) {
        const bundleBox = node('div', 'teklif-bundle-list');
        bundleBox.append(node('span', 'teklif-bundle-list__title', 'Kombin İçeriği:'));
        for (const bItem of this.offer.presentation.bundle_items) {
          const bQty = integer(bItem.quantity) && bItem.quantity > 0 ? bItem.quantity : 1;
          const bTitle = bItem.title_tr || bItem.title || '';
          const row = node('div', 'teklif-bundle-list__item');
          row.append(
            node('span', 'teklif-bundle-list__name', bQty + '× ' + bTitle),
            node('span', 'teklif-bundle-list__price', money(bItem.unit_price_minor * bQty))
          );
          bundleBox.append(row);
        }
        view.append(bundleBox);
      }

      const recommendedTier = primaryBenefitTier(displayTiers.filter(t => !t._baseline)) || displayTiers[displayTiers.length - 1];
      const recommendedQty = recommendedTier ? recommendedTier.quantity : null;

      const choices = node('div', 'teklif-options');
      choices.setAttribute('role', 'group');
      choices.setAttribute('aria-label', 'Adet ve toplam fiyat seçenekleri');
      choices.dataset.count = String(displayTiers.length);

      for (const tier of displayTiers) {
        const isSelected = state.quantity === tier.quantity;
        const isRecommended = tier.quantity === recommendedQty && tier.benefit_type !== 'NONE';
        const hasBenefit = tier.benefit_type !== 'NONE';
        const copy = this.buildTierCopy(tier, meta, activeUnitMinor);

        const button = node('button', 'teklif-option');
        button.type = 'button';
        button.dataset.quantity = String(tier.quantity);
        button.dataset.benefit = tier.benefit_type === 'EXISTING_FREE_SHIPPING' ? 'free-shipping' : (hasBenefit ? 'advantage' : 'standard');
        if (isRecommended) button.dataset.recommended = 'true';
        button.setAttribute('aria-pressed', String(isSelected));

        const top = node('div', 'teklif-option__top');
        const qtyGroup = node('span', 'teklif-option__qty-group');
        const check = node('span', 'teklif-option__check', isSelected ? '✓' : '');
        check.setAttribute('aria-hidden', 'true');
        const qtyLabel = hasBenefit && tier.quantity >= 2 ? tier.quantity + '+ Adet' : tier.quantity + ' Adet';
        qtyGroup.append(check, node('span', 'teklif-quantity', qtyLabel));
        top.append(qtyGroup);

        if (isRecommended) {
          top.append(node('span', 'teklif-badge teklif-badge--recommended', 'Önerilen'));
        } else if (isSelected) {
          top.append(node('span', 'teklif-badge teklif-badge--selected', 'Seçili'));
        }

        const priceGroup = node('div', 'teklif-option__pricing');
        const fullTotal = activeUnitMinor * tier.quantity;
        const effectiveTotal = activeUnitMinor === this.offer.base_unit_price_minor
          ? tier.total_minor
          : calculatePdpVariantPricing(this.offer, activeUnitMinor, tier.quantity).totalMinor;
        if (effectiveTotal < fullTotal) {
          priceGroup.append(node('s', 'teklif-strike-total', money(fullTotal)));
        } else if (tier.benefit_type === 'REFERENCE_PRICE_ADVANTAGE' && this.offer.presentation?.reference_price_minor) {
          priceGroup.append(node('s', 'teklif-strike-total', money(this.offer.presentation.reference_price_minor * tier.quantity)));
        }
        priceGroup.append(
          node('strong', 'teklif-total', money(effectiveTotal)),
          node('span', 'teklif-unit-price', tier.quantity > 1 ? 'Birim: ' + money(Math.round(effectiveTotal / tier.quantity)) : 'Tekli seçim')
        );

        const footer = node('div', 'teklif-option__footer');
        if (hasBenefit) {
          footer.append(
            benefitPill(copy.pill, 'teklif-benefit-pill--tier'),
            node('span', 'teklif-benefit', copy.footerSub)
          );
        } else {
          footer.append(node('span', 'teklif-benefit', 'Standart fiyat'));
        }

        button.append(top, priceGroup, footer);
        button.addEventListener('click', () => {
          this.selectTierQuantity(tier.quantity);
        });
        choices.append(button);
      }
      view.append(choices);

      const blockedHigherTier = this.offer.tiers.find(tier => !contractTiers.some(c => c.quantity === tier.quantity));
      const policy = this.offer.shipping_policy;
      let noteText = policy
        ? 'Seçili beden ve renkten sepete eklenir. 2 ve üzeri uygun ürün seçiminde standart kargo ücretsizdir; ödeme adımında otomatik uygulanır.'
        : 'Aynı seçili beden ve renkten eklenir. Toplam ürün fiyatıdır; kargo ödeme adımında hesaplanır.';
      if (blockedHigherTier && state.variant && integer(state.variant.max)) {
        noteText += ' Mevcut beden stoğu (' + state.variant.max + ' adet) nedeniyle üst adet seçeneği kapalıdır.';
      }
      view.append(node('p', 'teklif-note', noteText));

      this.hidden = false;
      this.syncProperties();
      this.syncFairy();
      setTimeout(() => syncFairyOfferYield(), 420);
      setTimeout(() => syncFairyOfferYield(), 1400);
      if (this.exposedHash !== this.offer.offer_hash) {
        this.exposedHash = this.offer.offer_hash;
        this.emit('render', null);
        this.exposureObserver?.disconnect();
        if ('IntersectionObserver' in window) {
          this.exposureObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting) && !this.hidden && !document.hidden && validContract(this.offer, this.context())) {
              this.emit('exposure', null);
              this.exposureObserver.disconnect();
            }
          });
          this.exposureObserver.observe(this);
        }
      }
    }

    publicExplanation() {
      if (this.hidden || !this.verified || !validContract(this.offer, this.context())) return null;
      const state = this.state();
      const tiers = this.offer.tiers.filter(tier => eligibleTier(this.offer, state.variant, tier.quantity, state.existing));
      return tiers.map(tier => (tier.quantity >= 2 ? tier.quantity + '+ adet ' : '1 adet ') + money(tier.total_minor) + (tier.benefit_type === 'EXISTING_FREE_SHIPPING' ? ' (Kargo Bizden · Standart Kargo: 0 TL)' : '; ' + tier.claim.toLowerCase())).join('. ') + '. Kargo avantajı ödeme adımında otomatik uygulanır.';
    }
  }

  class LasiriusCampaignHub extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.controller = new AbortController();
      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        const opts = { signal: this.controller.signal, passive: true };
        window.addEventListener('resize', () => syncFairyOfferYield(), opts);
        window.addEventListener('scroll', () => syncFairyOfferYield(), opts);
      }
      this.read();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.expiryTimer);
    }

    async read() {
      const generation = this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      clearTimeout(this.expiryTimer);
      const view = this.querySelector('[data-campaign-view]') || this;
      view.replaceChildren();
      try {
        const script = this.querySelector('[data-campaign-candidates]');
        if (!script) return;
        const candidates = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(candidates) || !candidates.length) return;
        const activeItems = [];
        let earliestUntil = Infinity;
        const seenProducts = new Set();

        for (const item of candidates) {
          if (!item || !item.offer || !item.product_gid || seenProducts.has(item.product_gid)) continue;
          const ctx = { product: item.product_gid, shop: item.shop_domain, currency: item.currency || 'TRY', isStoreWide: item.offer?.scope === 'STORE_WIDE' || item.offer?.presentation?.scope === 'STORE_WIDE' || item.offer?.scope_target === 'tum-katalog' };
          if (!validContract(item.offer, ctx)) continue;
          if (!(await validSeal(item.offer))) continue;
          if (!this.isConnected || generation !== this.generation) return;
          const isStoreWide = item.offer?.scope === 'STORE_WIDE' || item.offer?.presentation?.scope === 'STORE_WIDE' || item.offer?.scope_target === 'tum-katalog';
          const hasStock = variants.some(v => v.available === true && (isStoreWide || eligibleTier(item.offer, v, benefitTier.quantity, 0)));
          if (!hasStock) continue;
          seenProducts.add(item.product_gid);
          const until = Math.min(date(item.offer.ends_at), item.offer.shipping_policy ? date(item.offer.shipping_policy.valid_until) : Infinity);
          if (until < earliestUntil) earliestUntil = until;
          activeItems.push({ ...item, benefitTier, until, meta: resolveTemplateMeta(item.offer) });
        }

        if (!activeItems.length || !this.isConnected || generation !== this.generation) {
          this.hidden = true;
          view.replaceChildren();
          return;
        }

        if (Number.isFinite(earliestUntil)) {
          this.expiryTimer = setTimeout(() => this.read(), Math.max(1, earliestUntil - Date.now() + 1));
        }

        const groupedCampaigns = this.groupCampaignItems(activeItems);
        this.renderHub(view, groupedCampaigns);
        this.hidden = false;
        syncFairyOfferYield();
        setTimeout(() => syncFairyOfferYield(), 420);
        setTimeout(() => syncFairyOfferYield(), 1400);
      } catch (_) {
        this.hidden = true;
        view.replaceChildren();
      }
    }

    groupCampaignItems(items) {
      const byCategory = new Map();
      for (const item of items) {
        const slug = categorySlugForItem(item);
        const priceKey = item.benefitTier.benefit_type === 'EXISTING_FREE_SHIPPING'
          ? 'FREE_SHIPPING'
          : String(item.benefitTier.total_minor);
        const key = [
          slug,
          item.meta.family,
          item.benefitTier.quantity,
          item.benefitTier.benefit_type,
          priceKey,
        ].join('::');
        if (!byCategory.has(key)) {
          byCategory.set(key, { slug, items: [] });
        }
        byCategory.get(key).items.push(item);
      }

      const cards = [];
      for (const group of byCategory.values()) {
        const first = group.items.find(candidate => Boolean(candidate.image_url)) || group.items[0];
        const isExplicitCategory = first.offer.presentation?.scope === 'CATEGORY' || first.offer.presentation?.scope === 'COLLECTION';
        if (group.items.length > 1 || isExplicitCategory) {
          const collectionUrl = first.collection_url || (group.slug && group.slug !== 'all' ? '/collections/' + group.slug : (first.url || '/collections/all'));
          cards.push({
            kind: 'CATEGORY_CAMPAIGN',
            slug: group.slug,
            scopeLabel: first.offer.presentation?.scope_label_tr || categoryLabelForSlug(group.slug, first.category),
            collectionUrl,
            productCount: group.items.length,
            lead: first,
            sampleItems: group.items.slice(0, 2),
          });
        } else {
          cards.push({
            kind: 'PRODUCT_CAMPAIGN',
            slug: group.slug,
            scopeLabel: first.category || 'Lasirius Seçkisi',
            collectionUrl: first.url || '/collections/all',
            productCount: 1,
            lead: first,
            sampleItems: [first],
          });
        }
      }
      return cards;
    }

    renderHub(view, campaignCards) {
      const container = node('div', 'lasirius-campaigns');
      const header = node('header', 'lasirius-campaigns__header');
      const headerLeft = node('div', 'lasirius-campaigns__intro');
      const badgeRow = node('div', 'lasirius-campaigns__badge-row');
      badgeRow.append(
        node('span', 'lasirius-campaigns__eyebrow', '✦ LASIRIUS AYRICALIKLARI'),
        benefitPill('Şu An Aktif', 'teklif-benefit-pill--active-status')
      );
      headerLeft.append(
        badgeRow,
        node('h2', 'lasirius-campaigns__title', 'Seçili Koleksiyonlarda Aktif Avantajlar'),
        node('p', 'lasirius-campaigns__subtitle', 'Uygun koleksiyon ve ürün seçimlerinizde avantajınız sepette ve ödeme adımında otomatik uygulanır.')
      );
      header.append(headerLeft);
      container.append(header);

      const grid = node('div', 'lasirius-campaigns__grid');
      grid.dataset.campaignCount = String(campaignCards.length);

      for (const group of campaignCards) {
        const item = group.lead;
        const tier = item.benefitTier;
        const meta = item.meta;
        const homeCopy = resolveSurfaceOfferCopy(item.offer, 'HOME', {
          scopeKind: group.kind,
          scopeLabel: group.scopeLabel,
          slug: group.slug,
          eligibleCount: group.productCount,
          quantity: tier.quantity,
        });
        const card = node('article', 'lasirius-campaign-card');
        card.dataset.productGid = item.product_gid;
        card.dataset.offerId = item.offer.offer_id;
        card.dataset.templateFamily = meta.family;
        card.dataset.campaignScope = group.kind === 'CATEGORY_CAMPAIGN' ? 'CATEGORY' : 'PRODUCT';

        let primaryUrl = group.collectionUrl;
        if (group.kind === 'PRODUCT_CAMPAIGN') {
          try {
            const parsed = new URL(primaryUrl, window.location?.origin || 'https://lasirius.com');
            parsed.searchParams.set('merlin_offer_tier', String(tier.quantity));
            primaryUrl = parsed.pathname + parsed.search;
          } catch (_) { /* keep primaryUrl */ }
        }

        const displayHeading = group.kind === 'CATEGORY_CAMPAIGN'
          ? homeCopy.headline
          : cleanDisplayTitle(item.title);

        const mediaLink = node('a', 'lasirius-campaign-card__media');
        mediaLink.href = primaryUrl;
        mediaLink.setAttribute('aria-label', displayHeading);
        if (item.image_url) {
          const img = document.createElement('img');
          img.src = item.image_url;
          img.alt = displayHeading;
          img.loading = 'eager';
          img.decoding = 'async';
          img.className = 'lasirius-campaign-card__img';
          mediaLink.append(img);
        } else {
          mediaLink.append(node('div', 'lasirius-campaign-card__placeholder', '✦ LASIRIUS'));
        }
        const badgeCategoryText = group.kind === 'CATEGORY_CAMPAIGN' && group.productCount > 1
          ? group.scopeLabel + ' · ' + group.productCount + ' Model'
          : (item.category || group.scopeLabel);
        mediaLink.append(node('span', 'lasirius-campaign-card__category', badgeCategoryText));

        const body = node('div', 'lasirius-campaign-card__body');
        const topPills = node('div', 'lasirius-campaign-card__pills');
        topPills.append(
          benefitPill(homeCopy.pill, 'teklif-benefit-pill--campaign')
        );
        const windowText = formatRemainingWindow(item.until);
        if (windowText) {
          topPills.append(node('span', 'teklif-timer-pill', windowText));
        }

        const titleEl = node('h3', 'lasirius-campaign-card__title');
        const titleLink = node('a', 'lasirius-campaign-card__title-link', displayHeading);
        titleLink.href = primaryUrl;
        titleEl.append(titleLink);

        const descEl = node('p', 'lasirius-campaign-card__desc', homeCopy.subcopy);

        body.append(topPills, titleEl, descEl);

        if (group.kind === 'CATEGORY_CAMPAIGN' && group.sampleItems.length > 0) {
          const sampleRow = node('div', 'lasirius-campaign-card__samples');
          sampleRow.append(node('span', 'lasirius-campaign-card__samples-label', 'Seçili Modeller:'));
          for (const sample of group.sampleItems.slice(0, 2)) {
            let sampleHref = sample.url || primaryUrl;
            try {
              const u = new URL(sampleHref, window.location?.origin || 'https://lasirius.com');
              u.searchParams.set('merlin_offer_tier', String(tier.quantity));
              sampleHref = u.pathname + u.search;
            } catch (_) { /* keep sampleHref */ }
            const chip = node('a', 'lasirius-campaign-card__sample-link', shortenProductTitle(sample.title));
            chip.href = sampleHref;
            sampleRow.append(chip);
          }
          if (group.productCount > 2) {
            const allLink = node('a', 'lasirius-campaign-card__sample-link lasirius-campaign-card__sample-link--more', 'Tümünü Gör (' + group.productCount + ') →');
            allLink.href = primaryUrl;
            sampleRow.append(allLink);
          }
          body.append(sampleRow);
        }

        const footer = node('div', 'lasirius-campaign-card__footer');
        const priceMeta = node('div', 'lasirius-campaign-card__pricing');
        priceMeta.append(
          node('span', 'lasirius-campaign-card__price-label', homeCopy.footerLabel),
          node('strong', 'lasirius-campaign-card__price-value', homeCopy.footerValue),
          node('span', 'lasirius-campaign-card__shipping-tag', homeCopy.footerSub)
        );

        const cta = node('a', 'lasirius-campaign-card__cta');
        cta.href = primaryUrl;
        const arrow = node('span', 'lasirius-campaign-card__cta-arrow', '→');
        arrow.setAttribute('aria-hidden', 'true');
        cta.append(node('span', '', homeCopy.cta), arrow);

        footer.append(priceMeta, cta);
        body.append(footer);
        card.append(mediaLink, body);
        grid.append(card);
      }

      container.append(grid);
      view.append(container);
    }
  }

  class LasiriusCollectionOfferHub extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.controller = new AbortController();
      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        const opts = { signal: this.controller.signal, passive: true };
        window.addEventListener('resize', () => syncFairyOfferYield(), opts);
        window.addEventListener('scroll', () => syncFairyOfferYield(), opts);
      }
      this.read();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.expiryTimer);
    }

    async read() {
      const generation = this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      clearTimeout(this.expiryTimer);
      const view = this.querySelector('[data-collection-offer-view]') || this;
      view.replaceChildren();
      try {
        const script = this.querySelector('[data-collection-offer-candidates]');
        if (!script) return;
        const candidates = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(candidates) || !candidates.length) return;
        const activeItems = [];
        let earliestUntil = Infinity;
        for (const item of candidates) {
          if (!item || !item.offer || !item.product_gid) continue;
          const ctx = { product: item.product_gid, shop: item.shop_domain, currency: item.currency || 'TRY', isStoreWide: this.dataset.isStoreWide === 'true' || item.offer?.scope === 'STORE_WIDE' || item.offer?.presentation?.scope === 'STORE_WIDE' || item.offer?.scope_target === 'tum-katalog' };
          if (!validContract(item.offer, ctx)) continue;
          if (!(await validSeal(item.offer))) continue;
          if (!this.isConnected || generation !== this.generation) return;
          const variants = Array.isArray(item.variants) ? item.variants : [];
          const benefitTier = primaryBenefitTier(item.offer.tiers);
          if (!benefitTier) continue;
          const isStoreWide = this.dataset.isStoreWide === 'true' || item.offer.scope === 'STORE_WIDE' || item.offer.presentation?.scope === 'STORE_WIDE' || item.offer.scope_target === 'tum-katalog';
          const hasStock = variants.some(v => v.available === true && (isStoreWide || eligibleTier(item.offer, v, benefitTier.quantity, 0)));
          if (!hasStock) continue;
          const until = Math.min(date(item.offer.ends_at), item.offer.shipping_policy ? date(item.offer.shipping_policy.valid_until) : Infinity);
          if (until < earliestUntil) earliestUntil = until;
          activeItems.push({ ...item, benefitTier, until, meta: resolveTemplateMeta(item.offer) });
        }
        if (!activeItems.length || !this.isConnected || generation !== this.generation) {
          this.hidden = true;
          view.replaceChildren();
          return;
        }
        if (Number.isFinite(earliestUntil)) {
          this.expiryTimer = setTimeout(() => this.read(), Math.max(1, earliestUntil - Date.now() + 1));
        }
        this.renderCollectionBanner(view, activeItems);
        this.hidden = false;
        syncFairyOfferYield();
        setTimeout(() => syncFairyOfferYield(), 420);
        setTimeout(() => syncFairyOfferYield(), 1400);
      } catch (_) {
        this.hidden = true;
        view.replaceChildren();
      }
    }

    renderCollectionBanner(view, activeItems) {
      const lead = activeItems[0];
      const tier = lead.benefitTier;
      const meta = lead.meta;
      const collectionTitle = this.dataset.collectionTitle || 'Koleksiyon';
      const collectionHandle = this.dataset.collectionHandle || categorySlugForItem(lead);
      const count = activeItems.length;
      const isStoreWide = lead.offer.scope === 'STORE_WIDE' || lead.offer.presentation?.scope === 'STORE_WIDE' || lead.offer.scope_target === 'tum-katalog' || this.dataset.isStoreWide === 'true';
      const colCopy = resolveSurfaceOfferCopy(lead.offer, 'COLLECTION', {
        scopeLabel: collectionTitle,
        slug: collectionHandle,
        eligibleCount: count,
        quantity: tier.quantity,
        isStoreWide: isStoreWide,
      });

      const card = node('div', 'lasirius-collection-offer');
      card.dataset.templateFamily = meta.family;
      card.dataset.eligibleProductCount = String(count);

      const eyebrowText = isStoreWide
        ? '✦ MAĞAZA GENELİ AVANTAJ · 2 VE ÜZERİ SEÇİME KARGO BİZDEN'
        : '✦ KATEGORİYE ÖZEL AVANTAJ · ' + count + ' UYGUN MODEL';
      const topRow = node('div', 'lasirius-collection-offer__top');
      topRow.append(
        benefitPill(colCopy.pill, 'teklif-benefit-pill--collection'),
        node('span', 'lasirius-collection-offer__eyebrow', eyebrowText)
      );
      const windowText = formatRemainingWindow(lead.until);
      if (windowText) {
        topRow.append(node('span', 'teklif-timer-pill', windowText));
      }

      const content = node('div', 'lasirius-collection-offer__content');
      content.append(
        topRow,
        node('strong', 'lasirius-collection-offer__title', colCopy.headline),
        node('p', 'lasirius-collection-offer__desc', colCopy.subcopy)
      );

      let firstProductUrl = '#ResultsList';
      if (typeof document !== 'undefined' && !document.getElementById('ResultsList') && lead.url) {
        firstProductUrl = lead.url;
        try {
          const parsed = new URL(firstProductUrl, window.location?.origin || 'https://lasirius.com');
          parsed.searchParams.set('merlin_offer_tier', String(tier.quantity));
          firstProductUrl = parsed.pathname + parsed.search;
        } catch (_) { /* keep firstProductUrl */ }
      }

      const actionCol = node('div', 'lasirius-collection-offer__action');
      const priceBadge = node('div', 'lasirius-collection-offer__price-box');
      priceBadge.append(
        node('span', 'lasirius-collection-offer__price-label', colCopy.footerLabel),
        node('strong', 'lasirius-collection-offer__price-value', colCopy.footerValue),
        node('span', 'lasirius-collection-offer__shipping-note', colCopy.footerSub)
      );
      const ctaLink = node('a', 'lasirius-collection-offer__cta', colCopy.cta);
      ctaLink.href = firstProductUrl;
      actionCol.append(priceBadge, ctaLink);

      card.append(content, actionCol);
      view.append(card);
    }
  }

  window.LasiriusOffers = Object.freeze({
    validContract,
    validSeal,
    eligibleTier,
    cartTier,
    canonical,
    formatRemainingWindow,
    resolveTemplateMeta,
    resolveSurfaceOfferCopy,
    validateCustomerCopyNaturalness,
    calculatePdpVariantPricing,
    shortenProductTitle,
    TEMPLATE_DESIGN_TOKENS,
    SEMANTIC_DANGER_ERROR_COLOR,
    OFFER_PSYCHOLOGY_DESIGN_VERSION,
    syncFairyOfferYield,
  });
  if (!customElements.get('lasirius-offer')) customElements.define('lasirius-offer', LasiriusOffer);
  if (!customElements.get('lasirius-campaign-hub')) customElements.define('lasirius-campaign-hub', LasiriusCampaignHub);
  if (!customElements.get('lasirius-collection-offer-hub')) customElements.define('lasirius-collection-offer-hub', LasiriusCollectionOfferHub);
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => syncFairyOfferYield(), { once: true });
    } else {
      syncFairyOfferYield();
    }
  }
})();
